import cn from 'classnames';
import { useEffect, useState } from 'react';

import { CheckoutContextExposedProps, useCheckoutContext } from '@zooplus/checkout-state';
import { FlatBox } from '@zooplus/ui-components';

import ContainerComponent from '../container-component';

import NewsletterSubscription from '@components/preview/newsletter-subscription/newsletter-subscription';
import usePageTracking from '@hooks/use-adobe-tracking/use-page-tracking';
import usePreviewTracking from '@hooks/use-adobe-tracking/use-preview-tracking';
import { useTranslation } from '@hooks/use-translations';
import { CHECKOUT_STATE_PROJECT_ID, TRACKING_DATA_EVENT } from '@utilities/constants';

import { containers } from './containers';
import styles from './preview-containers-section.module.scss';

export type SetCompleteProps = (value?: boolean, shouldMoveToNext?: boolean) => void;
export type SetEditContainerProps = () => void;
export type EditProps = {
  name?: string | (() => string | string[]);
  shouldDisplayAlways: boolean;
};
export type ErrorProps = (props: ContentProps) => boolean;

export type ContentProps = {
  checkoutContext: CheckoutContextExposedProps;
  isContainerActive?: boolean;
  isContainerComplete?: boolean;
  setComplete?: SetCompleteProps;
  editContainer?: SetEditContainerProps;
  setCurrentStepActive?: () => void;
  arePrevContainersCompleted?: boolean;
};

interface ContainerDto extends ContainerType {
  _isComplete: boolean;
}

export type NameFuncProp = (prop: CheckoutContextExposedProps) => string | string[];
export type ContainerType = {
  name: string | NameFuncProp;
  Content: React.ComponentType<ContentProps>;
  dataTestId?: string;
  edit?: EditProps | boolean | ((prop: CheckoutContextExposedProps) => boolean);
  error?: ErrorProps;
};

const getContainersWithPrivateKeys = (): ContainerDto[] => {
  return containers.map((container) => ({ ...container, _isComplete: false }));
};

const PreviewContainersSection = () => {
  const { t } = useTranslation();
  const { trackPreviewUnsavedSnackbarAlert } = usePreviewTracking();
  const { trackEventUsingTrackerListener } = usePageTracking();
  const checkoutContext = useCheckoutContext(CHECKOUT_STATE_PROJECT_ID);

  const [activeContainerIndex, setActiveContainerIndex] = useState<number>(0);
  const [containersDto, setContainersDto] = useState<ContainerDto[]>(
    getContainersWithPrivateKeys(),
  );

  const { isLoading, setIsEditStepTriggered, isEditStepTriggered, setIsLastContainerActive } =
    checkoutContext;

  const lastContainerIndex = containersDto.length - 1;

  const evaluateContainerName = (value: string | string[]) => {
    switch (typeof value) {
      case 'string':
        return t(value);
      case 'object':
        if (Array.isArray(value) && value.length) {
          const parameters = value
            .slice(1)
            .reduce<{ [key: string]: string }>((acc, curr, index) => {
              acc[index] = curr;
              return acc;
            }, {});
          return t(value[0], parameters);
        }
        return '';
    }
  };

  useEffect(() => {
    if (isEditStepTriggered) {
      trackPreviewUnsavedSnackbarAlert();
    }
  }, [isEditStepTriggered]);

  useEffect(() => {
    setIsLastContainerActive?.(activeContainerIndex === lastContainerIndex);
  }, [activeContainerIndex]);

  useEffect(() => {
    trackEventUsingTrackerListener(TRACKING_DATA_EVENT.PAGE_VIEW);
  }, [trackEventUsingTrackerListener]);

  return (
    <FlatBox
      rounded
      data-testid="preview-containers-section"
      className={cn(styles.containersSectionWrapper, 'pp-p-0')}>
      {containersDto?.map(
        (
          { name, Content: ContainerContent, dataTestId, _isComplete, edit },
          currentContainersIndex,
        ) => {
          const containerName = typeof name === 'string' ? name : name(checkoutContext);
          const isContainerActive = activeContainerIndex === currentContainersIndex;

          const allPrevContainers = [...containersDto].filter(
            (_, index) => index < currentContainersIndex,
          );

          const arePrevContainersCompleted = allPrevContainers.every(
            ({ _isComplete }) => _isComplete,
          );

          const getEdit =
            typeof edit === 'boolean' || typeof edit === 'object' ? edit : edit?.(checkoutContext);

          const setCurrentStepActive = () => {
            setActiveContainerIndex(currentContainersIndex);
          };

          const handleContainerEdit = () => {
            if (!isLoading && currentContainersIndex !== lastContainerIndex) {
              setActiveContainerIndex(currentContainersIndex);
              activeContainerIndex !== lastContainerIndex && setIsEditStepTriggered?.(true);
            }
          };

          const handleContainerComplete = (value?: boolean, shouldMoveToNext: boolean = true) => {
            const tempContainers = [...containersDto];
            if (tempContainers && tempContainers[currentContainersIndex]) {
              tempContainers[currentContainersIndex]._isComplete = !!value;
            }
            setContainersDto(tempContainers);
            shouldMoveToNext && moveToNext();
          };

          const containerProps: ContentProps = {
            isContainerActive,
            isContainerComplete: _isComplete,
            setComplete: handleContainerComplete,
            editContainer: handleContainerEdit,
            setCurrentStepActive,
            arePrevContainersCompleted,
            checkoutContext,
          };

          const moveToNext = () => {
            const nextIncompleteContainerIndex = containersDto.findIndex(
              (item) => !item?._isComplete || item?.error?.(containerProps),
            );
            if (nextIncompleteContainerIndex !== -1) {
              setActiveContainerIndex(nextIncompleteContainerIndex);
              setIsEditStepTriggered?.(false);
              return;
            }

            const areAllContainersComplete = containersDto.every(
              ({ _isComplete, error }) => _isComplete && !error?.(containerProps),
            );
            if (areAllContainersComplete) {
              setIsEditStepTriggered?.(false);
              setActiveContainerIndex(lastContainerIndex);
            }
          };

          return (
            <ContainerComponent
              key={currentContainersIndex}
              containerName={evaluateContainerName(containerName)}
              containerNumber={currentContainersIndex}
              dataTestId={dataTestId}
              isContainerActive={isContainerActive}
              isContainerComplete={_isComplete}
              editContainer={handleContainerEdit}
              edit={getEdit}
              evaluateContainerName={evaluateContainerName}
              activeContainerIndex={activeContainerIndex}
              isLastContainer={currentContainersIndex === lastContainerIndex}>
              <ContainerContent {...containerProps} />
            </ContainerComponent>
          );
        },
      )}
      <NewsletterSubscription />
    </FlatBox>
  );
};

export default PreviewContainersSection;
